/**
 * Drizzle query builders + query-key factory for the course domain (Aula).
 *
 * Local-first consumption: the course tree (course/unit/lesson/block),
 * enrollment, progress and learner stats live in PowerSync/WASQLite
 * and are read offline. The PUBLIC catalog, by contrast, is server-authoritative
 * (REST `/api/catalog`, guest-first) — see `catalog.ts`.
 */

import {
  and,
  asc,
  block,
  count,
  course,
  desc,
  enrollment,
  eq,
  isNull,
  leaderboard_entry,
  learner_stats,
  lesson,
  lesson_progress,
  type PowerSyncDrizzleDB,
  unit,
} from "@aula/powersync";

/** Cache keys for the course domain (TanStack Query). */
export const courseQueryKeys = {
  all: ["courses"] as const,
  authored: (userId: string | undefined) => ["courses", "authored", userId] as const,
  tree: (courseId: string | undefined) => ["courses", "tree", courseId] as const,
  units: (courseId: string | undefined) => ["courses", "units", courseId] as const,
  lessons: (courseId: string | undefined) => ["courses", "lessons", courseId] as const,
  blocks: (lessonId: string | undefined) => ["courses", "blocks", lessonId] as const,
  enrollments: (userId: string | undefined) => ["courses", "enrollments", userId] as const,
  enrollment: (userId: string | undefined, courseId: string | undefined) =>
    ["courses", "enrollment", userId, courseId] as const,
  progress: (userId: string | undefined, courseId: string | undefined) =>
    ["courses", "progress", userId, courseId] as const,
  learnerStats: (userId: string | undefined) => ["courses", "learner-stats", userId] as const,
  certificates: (userId: string | undefined) => ["courses", "certificates", userId] as const,
  certificate: (userId: string | undefined, courseId: string | undefined) =>
    ["courses", "certificate", userId, courseId] as const,
  certificateById: (certId: string | undefined) =>
    ["courses", "certificate-by-id", certId] as const,
  /** PUBLIC verification by id (REST, server-authoritative) — not PowerSync. */
  certificateVerification: (certId: string | undefined) =>
    ["certificates", "verify", certId] as const,
  reviewQueue: (userId: string | undefined) => ["courses", "review-queue", userId] as const,
  leaderboard: (leagueId: string | undefined) => ["courses", "leaderboard", leagueId] as const,
  leaderboardOptIn: (userId: string | undefined) =>
    ["courses", "leaderboard-opt-in", userId] as const,
  myOrganizations: () => ["courses", "my-organizations"] as const,
  /** PUBLIC catalog (REST, server-authoritative) — not PowerSync. */
  catalogList: (q: string) => ["catalog", "list", q] as const,
  catalogCourse: (courseId: string) => ["catalog", "course", courseId] as const,
};

/** Courses the user AUTHORS (includes drafts). For `/authoring`. */
export function getMyAuthoredCoursesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(course)
    .where(and(eq(course.author_id, userId), isNull(course.deleted_at)))
    .orderBy(asc(course.updated_at));
}

/** Course metadata (authoring or already-synced local catalog). */
export function getCourseQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db.select().from(course).where(eq(course.id, courseId)).limit(1);
}

/** Units of a course, ordered. */
export function getUnitsQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(unit)
    .where(and(eq(unit.course_id, courseId), isNull(unit.deleted_at)))
    .orderBy(asc(unit.position), asc(unit.created_at));
}

/** Lessons of a course, ordered (groupable by `unit_id`). */
export function getLessonsQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db
    .select()
    .from(lesson)
    .where(and(eq(lesson.course_id, courseId), isNull(lesson.deleted_at)))
    .orderBy(asc(lesson.position), asc(lesson.created_at));
}

/** Blocks of a lesson, ordered. */
export function getBlocksQuery(db: PowerSyncDrizzleDB, lessonId: string) {
  return db
    .select()
    .from(block)
    .where(and(eq(block.lesson_id, lessonId), isNull(block.deleted_at)))
    .orderBy(asc(block.position), asc(block.created_at));
}

/** The user's enrollments. */
export function getMyEnrollmentsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(enrollment)
    .where(and(eq(enrollment.user_id, userId), isNull(enrollment.deleted_at)))
    .orderBy(asc(enrollment.created_at));
}

/** Is the user enrolled in a course? (single row). */
export function getEnrollmentQuery(db: PowerSyncDrizzleDB, userId: string, courseId: string) {
  return db
    .select()
    .from(enrollment)
    .where(and(eq(enrollment.user_id, userId), eq(enrollment.course_id, courseId)))
    .limit(1);
}

/** Learner progress in a course (all lessons touched). */
export function getCourseProgressQuery(db: PowerSyncDrizzleDB, userId: string, courseId: string) {
  return db
    .select()
    .from(lesson_progress)
    .where(and(eq(lesson_progress.user_id, userId), eq(lesson_progress.course_id, courseId)));
}

/**
 * Learner progress across ALL their courses with each lesson's title (for the
 * SRS review queue). Local-first: `lesson_progress` and `lesson` already arrive via
 * the learning streams; the join resolves on the local replica.
 */
export function getMyReviewProgressQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      lesson_id: lesson_progress.lesson_id,
      course_id: lesson_progress.course_id,
      status: lesson_progress.status,
      score: lesson_progress.score,
      attempts: lesson_progress.attempts,
      completed_at: lesson_progress.completed_at,
      updated_at: lesson_progress.updated_at,
      lesson_title: lesson.title,
    })
    .from(lesson_progress)
    .innerJoin(lesson, eq(lesson_progress.lesson_id, lesson.id))
    .where(eq(lesson_progress.user_id, userId));
}

/** Server-authoritative gamification stats for the learner (XP/streak). */
export function getLearnerStatsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(learner_stats).where(eq(learner_stats.user_id, userId)).limit(1);
}

/**
 * Leaderboard of a league (`'global'` or an organization id). Only entries
 * with `opted_in = true` arrive (the server filters by consent on the
 * stream), so anyone who did not opt in does NOT appear here. The view computes
 * ranking/tiers with the core `gamification/leagues.ts` (rankLeaderboard + assignTier).
 */
export function getLeaderboardQuery(db: PowerSyncDrizzleDB, leagueId: string) {
  return db
    .select()
    .from(leaderboard_entry)
    .where(eq(leaderboard_entry.league_id, leagueId))
    .orderBy(desc(leaderboard_entry.total_xp))
    .limit(100);
}

/**
 * Does the guest have local LEARNING data worth migrating to the real
 * account on sign-up? Counts their enrollments — in Aula
 * guest-first, a guest consumes courses offline and accumulates enrollment/progress
 * before creating an account. Replaces the old todo-count (`todo`) from
 * the Aula canon, already contracted. Used by the "Keep local data?" prompt.
 */
export function getLocalLearningDataCountQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select({ total: count() }).from(enrollment).where(eq(enrollment.user_id, userId));
}
